import { applyMode, Mode } from '@amzn/awsui-global-styles';
import { faChevronDown, faChevronUp, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@amzn/awsui-components-core/icon';
import { eCacheKeys, eLocalStorageKeys, generateProfilePictureUrl } from 'src/constants/AppConstants';
import { useAskQuick } from 'src/components/ask-quick/AskQuickContext';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import QUICKSUITE_LOGO_DEFAULT from '../../../images/quicksuite-logo-default.svg';
import QUICKSUITE_LOGO_ACTIVE from '../../../images/quicksuite-logo-active.svg';
import { useAuth } from '../../context/AuthContextProvider';
import { PROFILE_DROPDOWN_ITEMS } from './ProfileDropdownConstants';
import { DFPProfileComponent } from './DFPProfileComponent';
import { getEmployeeByLogin } from 'src/api/papi-service';
import { mapEmployeeToProfileInfo, BADGE_COLOR_MAP } from 'src/utilities/profile-utils';
import { EmbeddedAppInfo } from 'src/hooks/useEmbeddedApp';

interface TopNavActionsProps {
  embeddedApp: EmbeddedAppInfo | null;
  sendMessageToEmbed: (type: string, payload?: Record<string, unknown>) => boolean;
}

export const TopNavActions: React.FC<TopNavActionsProps> = ({ embeddedApp, sendMessageToEmbed }) => {
  const { Alias: userAlias } = useAuth();
  const { togglePanel: toggleAskQuick, isOpen: isAskQuickOpen } = useAskQuick();
  const [isDarkModeEnabled, setIsDarkModeEnabled] = usePersistedState(eLocalStorageKeys.IsDarkModeEnabled, false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [badgeColor, setBadgeColor] = useState<string | null>(null);
  const profilePanelRef = useRef<HTMLDivElement>(null);
  const darkModeBeforeAskQuick = useRef<boolean | null>(null);

  useEffect(() => {
    applyMode(isDarkModeEnabled ? Mode.Dark : Mode.Light);
  }, [isDarkModeEnabled]);

  // Pre-warm profile info cache on page load
  useEffect(() => {
    const prewarmProfileCache = async () => {
      try {
        const response = await getEmployeeByLogin(userAlias, 'job');
        if (!response.employee) return;
        const info = mapEmployeeToProfileInfo(response.employee);
        if (info.badgeColor && BADGE_COLOR_MAP[info.badgeColor]) {
          setBadgeColor(BADGE_COLOR_MAP[info.badgeColor]);
        }
      } catch {
        // Best-effort pre-warm; DFPProfileComponent has its own fallback fetch on cache miss
      }
    };
    prewarmProfileCache();
  }, [userAlias]);

  // Close profile panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profilePanelRef.current && !profilePanelRef.current.contains(event.target as Node)) {
        setShowProfilePanel(false);
      }
    };
    if (showProfilePanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfilePanel]);

  const handleDarkModeToggle = useCallback(() => {
    const newDarkMode = !isDarkModeEnabled;
    setIsDarkModeEnabled(newDarkMode);
    sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: newDarkMode });
  }, [isDarkModeEnabled, setIsDarkModeEnabled, sendMessageToEmbed]);

  useEffect(() => {
    if (isAskQuickOpen && isDarkModeEnabled) {
      darkModeBeforeAskQuick.current = true;
      setIsDarkModeEnabled(false);
      sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: false });
    } else if (!isAskQuickOpen && darkModeBeforeAskQuick.current) {
      darkModeBeforeAskQuick.current = null;
      setIsDarkModeEnabled(true);
      sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: true });
    }
  }, [isAskQuickOpen, isDarkModeEnabled, setIsDarkModeEnabled, sendMessageToEmbed]);

  return (
    <div className="dfp-top-nav__right">
      {embeddedApp?.url && (
        <>
          <button
            className="dfp-top-nav__utility-btn"
            onClick={() => embeddedApp.url && window.open(embeddedApp.url, '_blank', 'noopener,noreferrer')}
            aria-label={`Open ${embeddedApp.name || 'application'} in a new tab`}
          >
            <span style={{ transform: 'scale(1.15)', display: 'flex', alignItems: 'center', color: '#ffffff' }}>
              <Icon name="external" />
            </span>
          </button>
          <div className="dfp-top-nav__divider-v" />
        </>
      )}
      <button
        className="dfp-top-nav__utility-btn"
        onClick={handleDarkModeToggle}
        aria-label={`Switch to ${isDarkModeEnabled ? 'light' : 'dark'} mode`}
        disabled={isAskQuickOpen}
      >
        <FontAwesomeIcon icon={isDarkModeEnabled ? faMoon : faSun} />
      </button>
      <div className="dfp-top-nav__divider-v" />
      <button
        className="dfp-top-nav__utility-btn"
        onClick={toggleAskQuick}
        aria-label="Toggle QuickSuite panel"
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        <img src={isAskQuickOpen ? QUICKSUITE_LOGO_ACTIVE : QUICKSUITE_LOGO_DEFAULT} alt="QuickSuite" className="dfp-top-nav__quicksuite-logo" />
      </button>
      <div className="dfp-top-nav__divider-v" />
      <div className="avatar-dropdown" ref={profilePanelRef}>
        <button
          className="avatar-dropdown__button"
          onClick={() => setShowProfilePanel((prev) => !prev)}
          aria-expanded={showProfilePanel}
          aria-label="Toggle profile panel"
        >
          <img
            src={generateProfilePictureUrl(userAlias)}
            alt={userAlias}
            className="avatar-dropdown__image"
            style={badgeColor ? { borderColor: badgeColor } : undefined}
          />
          <FontAwesomeIcon icon={showProfilePanel ? faChevronUp : faChevronDown} className="avatar-dropdown__chevron" />
        </button>
        {showProfilePanel && (
          <div className="profile-panel">
            <DFPProfileComponent profile_items={PROFILE_DROPDOWN_ITEMS} onCancel={() => setShowProfilePanel(false)} />
          </div>
        )}
      </div>
    </div>
  );
};
